import type {
  AdminAlertItem,
  AdminFlagItem,
  Alert,
  AlertSeverity,
  ClientType,
  FeatureFlag,
} from '@/types/controlCenter'

/** API value (e.g. "CRITICAL") → UI value (e.g. "critical") */
export const SEVERITY_MAP: Record<string, AlertSeverity> = {
  CRITICAL: 'critical',
  WARNING: 'warning',
  SUCCESS: 'success',
  INFO: 'info',
}

/** UI value (e.g. "critical") → API value (e.g. "CRITICAL") */
export const SEVERITY_MAP_INVERSE: Record<AlertSeverity, string> =
  Object.fromEntries(
    Object.entries(SEVERITY_MAP).map(([api, ui]) => [ui, api]),
  ) as Record<AlertSeverity, string>

export function mapAdminFlags(
  items: AdminFlagItem[],
  clientMap: Record<number, string>,
): FeatureFlag[] {
  return items.map((item) => ({
    id: String(item.id),
    name: item.name,
    description: item.description,
    status: item.active ? ('enabled' as const) : ('disabled' as const),
    environment: 'production' as const,
    lastModified: item.updated_at,
    createdAt: item.created_at,
    client: clientMap[item.client_id] as ClientType | undefined,
    global: item.global,
  }))
}

export function mapAdminAlerts(items: AdminAlertItem[]): Alert[] {
  return items.map((item) => ({
    id: String(item.id),
    name: item.name,
    description: item.message,
    url: item.path,
    severity: (() => {
      const mapped = SEVERITY_MAP[item.type]
      if (!mapped)
        throw new Error(`Unknown alert severity from API: "${item.type}"`)
      return mapped
    })(),
    status: item.active ? ('active' as const) : ('inactive' as const),
    createdAt: item.created_at,
    endDate: item.end_date,
    clientIds: item.client_ids ?? [],
    applicationId: item.application_id ?? null,
    supportsClient: item.supports_client ?? false,
    dismissible: item.dismissible ?? false,
  }))
}
